import { useQuery } from '@tanstack/react-query';
import {
  BookOpen,
  ClipboardList,
  FileText,
  Layers,
  MonitorPlay,
  PlayCircle,
  Presentation,
  Search,
  StickyNote,
} from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';

import { api, unwrap } from '@/api/client';
import type { components } from '@/api/schema';
import { AVATARES } from '@/lib/avatares';
import { useStagger } from '@/lib/motion';

import { Cargando, ErrorCarga } from '../curso/componentes';

type Resultados = components['schemas']['SearchResults'];
type Tipo = Resultados['groups'][number]['type'];

const TIPOS: Record<Tipo, { label: string; icono: typeof Search }> = {
  class: { label: 'Clases y talleres', icono: MonitorPlay },
  presentation: { label: 'Presentaciones', icono: Presentation },
  flashcard: { label: 'Flash cards', icono: Layers },
  videotip: { label: 'Video tips', icono: PlayCircle },
  gpc_summary: { label: 'Resúmenes GPC', icono: StickyNote },
  gpc_guide: { label: 'Guías GPC', icono: FileText },
  manual: { label: 'Manuales', icono: BookOpen },
  exam: { label: 'Exámenes', icono: ClipboardList },
};

/** Búsqueda en Consalud (port de `inicio/busqueda`), agrupada por tipo y con enlaces reales. */
export function BusquedaPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q')?.trim() ?? '';
  const [texto, setTexto] = useState(q);
  const root = useRef<HTMLDivElement>(null);
  const { data, error, isFetching } = useQuery({
    queryKey: ['busqueda', q],
    queryFn: () => unwrap<Resultados>(api.GET('/api/v1/search', { params: { query: { q } } })),
    enabled: q.length >= 3,
    staleTime: 60_000,
  });
  useStagger(root, '[data-card]', { step: 30, deps: [data] });

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    const t = texto.trim();
    if (t.length >= 3) setParams({ q: t });
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <h1 className="titulo-seccion">
        <span className="subrayado-verde">Búsqueda en Consalud</span>
      </h1>
      <form onSubmit={enviar} className="flex gap-2" role="search">
        <label className="relative flex-1">
          <span className="sr-only">Buscar</span>
          <Search size={18} className="absolute top-1/2 left-3 -translate-y-1/2 text-texto-3" />
          <input
            className="input-vidrio w-full pl-10"
            placeholder="Flashcards, videos, guías GPC, algoritmos…"
            value={texto}
            minLength={3}
            maxLength={80}
            onChange={(e) => setTexto(e.target.value)}
          />
        </label>
        <button type="submit" className="btn-cs btn-primario" disabled={texto.trim().length < 3}>
          Buscar
        </button>
      </form>
      {q.length === 0 && (
        <section className="panel-vidrio mx-auto mt-16 max-w-xl text-center">
          <img
            src={AVATARES.doctorSenala}
            alt=""
            className="pointer-events-none mx-auto -mt-24 mb-2 h-28 w-auto sm:-mt-28 sm:h-32"
          />
          <p className="text-texto-2">
            Encuentra clases, flashcards, video tips, guías y resúmenes GPC, manuales y exámenes en
            un solo lugar.
          </p>
        </section>
      )}
      {q.length > 0 && q.length < 3 && (
        <p className="text-sm text-texto-2">Escribe al menos 3 letras.</p>
      )}
      {isFetching && <Cargando alto="h-40" />}
      {error && <ErrorCarga error={error} />}
      {data && !isFetching && (
        <div ref={root} className="space-y-6">
          <p className="text-sm text-texto-2" aria-live="polite">
            {data.total === 0 ? (
              <>No encontramos resultados para «{data.query}».</>
            ) : (
              <>
                {data.total} {data.total === 1 ? 'resultado' : 'resultados'} para «{data.query}».
              </>
            )}
          </p>
          {data.groups.map((g) => {
            const T = TIPOS[g.type];
            return (
              <section key={g.type} className="space-y-2">
                <h2 className="flex items-center gap-2 text-sm font-extrabold tracking-wide text-verde-texto uppercase">
                  <T.icono size={16} /> {T.label}
                </h2>
                <ul className="grid gap-2 sm:grid-cols-2">
                  {g.items.map((r) => (
                    <li key={`${r.type}-${String(r.id)}`} data-card>
                      <Link
                        to={r.path}
                        className="card-vidrio card-vidrio-hover flex items-center gap-3 p-3"
                      >
                        {/* Bloque de ícono a la izquierda (`resultImage` del legacy). */}
                        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-azul-600 text-white">
                          <T.icono size={24} />
                        </span>
                        <span className="min-w-0">
                          <span className="block font-bold">{r.title}</span>
                          {r.subtitle && <span className="text-xs text-texto-3">{r.subtitle}</span>}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
